import React, { useState, useMemo } from 'react';
import { Modal, Button, Spinner } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faBell } from '@fortawesome/free-solid-svg-icons';
import { usePushNotification } from '../../hooks/usePushNotification';
import './push-notification-modal.scss';

/**
 * Detect if the app is running as a PWA (installed on device)
 */
const isPWA = (): boolean => {
  // Check for standalone display mode (most reliable)
  if (window.matchMedia('(display-mode: standalone)').matches) {
    return true;
  }
  // iOS Safari standalone mode
  if ((navigator as any).standalone === true) {
    return true;
  }
  return false;
};

/**
 * localStorage key for tracking "Not now" dismissals per platform.
 * Using localStorage instead of server-side seen-modals so that:
 * - PWA reinstall wipes it (modal re-appears — user needs to re-grant permission)
 * - "Not now" persists across refreshes on the same install
 */
const DISMISSED_KEY_PREFIX = 'push_notification_dismissed_';

/**
 * Modal that prompts users to enable push notifications.
 * Shows if:
 * - Push notifications are supported
 * - User hasn't already set up push notifications
 * - User hasn't dismissed this modal on this device/install
 * - Permission wasn't denied
 *
 * Uses localStorage for dismissal tracking so PWA reinstall clears it.
 */
export const PushNotificationModal: React.FC = () => {
  const {
    isSupported,
    isFullySetup,
    isDenied,
    isRequesting,
    requestPermissionAndSubscribe,
  } = usePushNotification();

  const [isEnabling, setIsEnabling] = useState(false);

  // Determine dismissal key based on platform
  const dismissedKey = useMemo(
    () => DISMISSED_KEY_PREFIX + (isPWA() ? 'pwa' : 'web'),
    [],
  );

  const [isDismissed, setIsDismissed] = useState(() => {
    try {
      return localStorage.getItem(dismissedKey) === 'true';
    } catch {
      return false;
    }
  });

  const shouldShow = isSupported && !isFullySetup && !isDenied && !isDismissed;

  // Debug logging
  console.log('🔔 PushNotificationModal state:', {
    dismissedKey,
    isDismissed,
    isSupported,
    isFullySetup,
    isDenied,
    shouldShow,
  });

  const handleEnable = async () => {
    setIsEnabling(true);
    try {
      await requestPermissionAndSubscribe();
    } finally {
      setIsEnabling(false);
    }
  };

  const handleNotNow = () => {
    try {
      localStorage.setItem(dismissedKey, 'true');
    } catch {
      // localStorage may be unavailable in some contexts
    }
    setIsDismissed(true);
  };

  const isLoading = isEnabling || isRequesting;

  return (
    <Modal
      show={shouldShow}
      onHide={handleNotNow}
      centered
      backdrop='static'
      keyboard={false}
      className='push-notification-modal'
    >
      <Modal.Body>
        <div className='icon-container'>
          <div className='icon-circle'>
            <FontAwesomeIcon icon={faBell} className='bell-icon' />
          </div>
        </div>
        <div className='content'>
          <h2 className='title'>Stay in the loop</h2>
          <div className='text'>
            <p>
              Get notified when your Dreamie needs your input — even when you're
              not in the app.
            </p>
          </div>
        </div>
        <div className='actions'>
          <Button
            variant='outline-secondary'
            onClick={handleNotNow}
            className='button secondary'
            disabled={isLoading}
          >
            Not now
          </Button>
          <Button
            variant='primary'
            onClick={handleEnable}
            className='button primary'
            disabled={isLoading}
          >
            {isLoading ? (
              <Spinner animation='border' size='sm' />
            ) : (
              'Enable notifications'
            )}
          </Button>
        </div>
      </Modal.Body>
    </Modal>
  );
};

export default PushNotificationModal;
